import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Flag,
  Lightbulb,
  Scale,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Employment & labour law in Ghana (Act 651, contracts, NLC). */
export const EmploymentLabourLawOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_64px_-32px_rgba(15,23,42,0.2)] sm:rounded-[1.85rem]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/35"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-[5px]"
        style={{ background: `linear-gradient(180deg, ${color}, ${color}55)` }}
        aria-hidden
      />
      <div className="relative grid lg:grid-cols-12">
        <div className="flex flex-col justify-center px-6 py-10 sm:p-10 lg:col-span-7 lg:pl-11">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-[0.32em]" style={{ color }}>
              LEGAL · EMPLOYMENT &amp; LABOUR LAW (GHANA)
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline" aria-hidden />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-[11px] font-bold text-slate-600">
              <Users size={13} style={{ color }} aria-hidden />
              {data.funnelTagline}
            </span>
          </div>

          <h2 className="max-w-2xl text-[1.5rem] font-black leading-[1.2] tracking-tight text-slate-950 sm:text-[1.85rem]">
            {data.introduction.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-[1.8] text-slate-600 sm:text-base">{data.introduction.lead}</p>

          <div className="mt-6 rounded-xl border border-slate-200/80 bg-slate-50/60 p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.introduction.leadsToHeading}</p>
            <ul className="mt-3 space-y-2">
              {data.introduction.leadsTo.map((x) => (
                <li key={x} className="text-sm font-semibold text-slate-800">
                  • {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 rounded-xl border p-5 sm:p-6" style={{ borderColor: `${color}40`, background: `${color}0a` }}>
            <p className="text-sm font-bold text-slate-800">{data.introduction.mainLawLead}</p>
            <p className="mt-2 text-lg font-black text-slate-950 sm:text-xl">{data.introduction.actName}</p>
            <p className="mt-4 text-xs font-black uppercase tracking-wider text-slate-600">{data.introduction.protectsLead}</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {data.introduction.protects.map((p) => (
                <li
                  key={p}
                  className="rounded-full border px-3 py-1 text-xs font-bold"
                  style={{ borderColor: `${color}44`, background: `${color}12`, color }}
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {data.focusAreas.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-slate-200/90 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-700"
              >
                {tag}
              </span>
            ))}
          </div>

          <p className="mt-8 flex items-start gap-2 text-sm font-bold leading-relaxed text-slate-900">
            <Lightbulb size={16} className="mt-0.5 shrink-0 opacity-70" style={{ color }} aria-hidden />
            {data.introduction.closing}
          </p>
        </div>

        <div className="relative min-h-[16rem] border-t border-slate-100 lg:col-span-5 lg:border-l lg:border-t-0 lg:min-h-0">
          <img src={data.galleryImages[0]} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md">
              <Briefcase size={14} className="text-white" aria-hidden />
              <span className="text-[10px] font-black uppercase tracking-[0.24em] text-white/95">Fair work · Act 651</span>
            </div>
            <p className="mt-3 max-w-sm text-lg font-black leading-snug text-white [text-wrap:balance]">
              Contracts · termination · disputes · compensation.
            </p>
          </div>
        </div>
      </div>
    </motion.section>

    <section>
      <SectionHead eyebrow="First line of defence" title={data.contracts.title} subtitle={data.contracts.whatHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-sm font-semibold text-slate-700">{data.contracts.whatLead}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.contracts.defines.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.contracts.typesHeading}
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {data.contracts.types.map((t) => (
              <div key={t} className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5 text-sm font-semibold text-slate-800">
                • {t}
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-xl border border-emerald-200/70 bg-emerald-50/45 p-5">
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-950">
              <CheckCircle2 size={14} className="text-emerald-600" aria-hidden />
              {data.contracts.mustIncludeHeading}
            </p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-emerald-950/95">
              {data.contracts.mustInclude.map((m) => (
                <li key={m}>• {m}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-6">
            <p className="flex items-center gap-2 text-sm font-black text-amber-950">
              <AlertTriangle size={17} className="shrink-0 text-amber-600" aria-hidden />
              {data.contracts.problemsHeading}
            </p>
            <ul className="mt-3 space-y-1.5 text-sm font-semibold text-amber-950/95">
              {data.contracts.problems.map((p) => (
                <li key={p}>• {p}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.contracts.tipHeading}</p>
            <ListBlock items={data.contracts.tipItems} color={color} icon={Shield} />
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Dismissal" title={data.wrongfulTermination.title} subtitle={data.wrongfulTermination.whatHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
            <p className="text-sm font-semibold text-slate-800">{data.wrongfulTermination.whatBody}</p>
            <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-5">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500">What the law says</p>
              <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-800">{data.wrongfulTermination.lawSays}</p>
            </div>
            <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
              {data.wrongfulTermination.examplesHeading}
            </p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.wrongfulTermination.examples.map((e) => (
                <li key={e}>• {e}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border p-6" style={{ borderColor: `${color}44`, background: `${color}08` }}>
            <p className="text-xs font-black uppercase tracking-wider text-slate-800">{data.wrongfulTermination.importantHeading}</p>
            <p className="mt-2 text-sm font-semibold text-slate-800">{data.wrongfulTermination.importantBody}</p>
            <ul className="mt-4 space-y-2 text-sm font-bold text-slate-900">
              <li>• {data.wrongfulTermination.wrongfulLabel}</li>
              <li>• {data.wrongfulTermination.unfairLabel}</li>
            </ul>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.wrongfulTermination.groundsHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.wrongfulTermination.grounds.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
          <div className="mt-10 rounded-xl border border-emerald-200/75 bg-emerald-50/40 p-5">
            <p className="text-xs font-black uppercase tracking-wider text-emerald-950">{data.wrongfulTermination.rightsHeading}</p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-emerald-950/95">
              {data.wrongfulTermination.rights.map((r) => (
                <li key={r}>• {r}</li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-4">
            <Scale size={18} className="mt-0.5 shrink-0 opacity-90" style={{ color }} aria-hidden />
            <p className="text-sm font-semibold leading-relaxed text-slate-800">{data.wrongfulTermination.whereHelpLead}</p>
          </div>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-12">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8 lg:col-span-7">
        <SectionHead
          eyebrow="Day-to-day"
          title={data.workplaceDisputes.title}
          subtitle={data.workplaceDisputes.typesHeading}
          color={color}
        />
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {data.workplaceDisputes.types.map((t) => (
            <li key={t} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
              • {t}
            </li>
          ))}
        </ul>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.workplaceDisputes.resolutionHeading}
        </p>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-800">
          {data.workplaceDisputes.resolutionSteps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <div className="flex flex-col gap-4 lg:col-span-5">
        <div className="rounded-xl border border-amber-200/80 bg-amber-50/45 p-6">
          <p className="text-sm font-black text-amber-950">{data.workplaceDisputes.mistakesHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-amber-950/95">
            {data.workplaceDisputes.mistakes.map((m) => (
              <li key={m}>• {m}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.workplaceDisputes.bestHeading}</p>
          <ListBlock items={data.workplaceDisputes.bestItems} color={color} />
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Pay & benefits" title={data.compensation.title} color={color} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200/90 bg-white p-5">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.compensation.salaryTitle}
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-700">{data.compensation.salaryBody}</p>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-5">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.compensation.leaveTitle}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
            {data.compensation.leaveItems.map((l) => (
              <li key={l}>• {l}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-5">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.compensation.overtimeTitle}
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-700">{data.compensation.overtimeBody}</p>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-5">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.compensation.severanceTitle}</p>
          <p className="mt-2 text-sm font-semibold text-slate-700">{data.compensation.severanceBody}</p>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-red-200/65 bg-red-50/35 p-6">
        <p className="text-xs font-black uppercase tracking-wider text-red-950">{data.compensation.issuesHeading}</p>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-red-950/95">
          {data.compensation.issues.map((i) => (
            <li key={i}>• {i}</li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Exits" title={data.terminationProcess.title} subtitle={data.terminationProcess.mustHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <ul className="space-y-3">
            {data.terminationProcess.mustInclude.map((m) => (
              <li key={m} className="flex items-center gap-3 text-sm font-bold text-slate-900">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-black" style={{ background: `${color}18`, color }}>
                  ✓
                </span>
                {m}
              </li>
            ))}
          </ul>
          <p className="mt-10 text-xs font-black uppercase tracking-wider text-slate-500">{data.terminationProcess.typesHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.terminationProcess.types.map((t) => (
              <li key={t}>• {t}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center rounded-xl border border-slate-900/[0.1] bg-slate-900/[0.04] p-8">
          <p className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color }}>
            {data.terminationProcess.insightHeading}
          </p>
          <p className="mt-4 text-lg font-black leading-snug text-slate-950">{data.terminationProcess.insightBody}</p>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Workers" title={data.employeeRights.title} subtitle={data.employeeRights.coreHeading} color={color} />
        <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-800">
          {data.employeeRights.core.map((r) => (
            <li key={r}>• {r}</li>
          ))}
        </ul>
        <div className="mt-8 border-t border-slate-100 pt-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
            {data.employeeRights.principleHeading}
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-700">{data.employeeRights.principleLead}</p>
          <ul className="mt-3 space-y-1 text-sm font-bold text-slate-900">
            {data.employeeRights.principleBecause.map((b) => (
              <li key={b}>• {b}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.employerResponsibilities.title}</p>
        <p className="mt-2 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.employerResponsibilities.mustHeading}
        </p>
        <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-800">
          {data.employerResponsibilities.mustDo.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
        <div className="mt-8 rounded-xl border border-amber-200/75 bg-amber-50/40 p-5">
          <p className="text-xs font-black uppercase tracking-wider text-amber-950">{data.employerResponsibilities.failHeading}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-amber-950/95">
            {data.employerResponsibilities.failItems.map((f) => (
              <li key={f}>• {f}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Reality check" title={data.commonProblemsGhana.title} subtitle={data.commonProblemsGhana.intro} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.commonProblemsGhana.items.map((i) => (
            <li
              key={i}
              className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm font-semibold text-slate-900"
            >
              • {i}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[data.practicalTips.beforeJob, data.practicalTips.during, data.practicalTips.dispute, data.practicalTips.dismissed].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Protect yourself" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Editorial" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.articlesHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.articles.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.videosHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.videos.map((v) => (
              <li key={v}>• {v}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.leadCtasHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/55 p-8 text-white sm:p-10">
      <div className="pointer-events-none absolute inset-0 opacity-20 blur-3xl" style={{ background: color }} aria-hidden />
      <div className="relative z-10">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.scenario.eyebrow}
        </p>
        <h3 className="mt-3 text-xl font-black sm:text-2xl">{data.scenario.title}</h3>
        <p className="mt-4 text-sm font-bold text-white/90">{data.scenario.exampleLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.scenario.bullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase tracking-wider text-white/50">{data.scenario.optionsHeading}</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {data.scenario.options.map((o) => (
            <li
              key={o}
              className="rounded-full border border-white/20 bg-white/[0.08] px-3 py-1 text-xs font-bold text-white"
            >
              {o}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[data.funnelApplication.hook, data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map(
          (step) => (
            <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
                {step.label}
              </p>
              <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
            </div>
          ),
        )}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <ul className="mt-6 space-y-4">
          {data.finalTakeaway.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-base font-bold leading-relaxed text-white/95 sm:text-lg">
              <CheckCircle2 size={22} className="shrink-0 opacity-90" style={{ color }} aria-hidden />
              {b}
            </li>
          ))}
        </ul>
        <div className="mt-10 border-t border-white/10 pt-8">
          <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.bottomLine.title}
          </p>
          <p className="mt-3 text-lg font-black text-white sm:text-xl">{data.bottomLine.lead}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <p className="rounded-lg border border-white/15 bg-white/[0.06] p-4 text-center text-sm font-bold text-white sm:text-base">{data.bottomLine.smooth}</p>
            <span className="hidden text-center text-sm font-black text-white/50 sm:block">{data.bottomLine.and}</span>
            <p className="rounded-lg border border-red-500/30 bg-red-950/35 p-4 text-center text-sm font-bold text-red-100 sm:text-base">{data.bottomLine.stressful}</p>
          </div>
          <p className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/70">
            <ArrowRight size={16} style={{ color }} aria-hidden />
            {data.bottomLine.closing}
          </p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const EmploymentLabourLawArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Content Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const EmploymentLabourLawMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="ell-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Employment and labour law videos"
            items={videos}
            header={<h3 id="ell-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="ell-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Employment and labour law podcast episodes"
            items={podcasts}
            header={<h3 id="ell-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
